import {
  Asset,
  FeatureName,
  GrippContext,
  IssueCreationSource,
  useIsFeatureEnabled,
  useStorageKeys,
  useWorkspaceAsset,
} from '@gripp/shared-logic';
import {
  AssetHeader,
  BaseTheme,
  BottomComponentBar,
  Colors,
  KeyboardSafeView,
  Layout,
  RoutineButtons,
  RoutineExecutionProps,
  RoutineModal,
  Spacing,
  TopComponentBarCropCircle,
  tokens,
} from '@gripp/shared-ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, useWindowDimensions, View } from 'react-native';
import { PaperProvider, Text } from 'react-native-paper';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { SignatureScreenWeb } from '../../src/shared/components/signature/signatureScreenWeb';
import { Logo } from '../shared/components/logo';
import { MediaPicker } from '../shared/components/media/mediaPicker';
import { localStorageHandler } from '../shared/localStorageHandler';
import { SentryWebLogger } from '../shared/logging/sentryWebLogger';
import { ActionButtons } from './actionButtons';
import { AnonymousReported } from './anonymousReported';

type AnonymousAssetProps = {
  asset: Asset;
  tagCode: string;
};

export const AnonymousAsset = (props: AnonymousAssetProps) => {
  const { t } = useTranslation();
  const [showReported, toggleShowReported] = useState<boolean>(false);
  const [reportedMessage, setReportedMessage] = useState<{
    message: string;
    messageDownloadApp: string;
  }>();
  const { workspaceAsset } = useWorkspaceAsset(props.asset.id);
  const [routineExecutionProps, setRoutineExecutionProps] = useState<
    RoutineExecutionProps | undefined
  >();
  const [executeModalVisible, setExecuteModalVisible] = useState(false);
  const { width } = useWindowDimensions();
  const isMobile = width < 768;
  const storageKeys = useStorageKeys();
  const [anonymousIssueIds, setAnonymousIssueIds] = useState<string[]>([]);
  const isCropCircleRedesignEnabled = useIsFeatureEnabled(
    FeatureName.CropCircleRedesign
  );

  const onIssueCompleted = (message: string, messageDownloadApp: string) => {
    setReportedMessage({ message, messageDownloadApp });
    const loadIssueIds = async () => {
      const stored = await localStorageHandler.getItem(
        storageKeys.anonymousIssueIds
      );
      if (stored) setAnonymousIssueIds(JSON.parse(stored));
    };
    loadIssueIds();
    toggleShowReported(true);
  };

  const onRoutineCompleted = async () => {
    setReportedMessage({
      message: t('issue.anonymous.routineCompleted'),
      messageDownloadApp: t('issue.anonymous.routineCompletedDownloadGrippApp'),
    });
    toggleShowReported(true);
  };

  function onRoutineSelected(props: RoutineExecutionProps): void {
    setRoutineExecutionProps(props);
    setExecuteModalVisible(true);
  }

  const renderCropCircleBody = () => {
    if (!workspaceAsset) return null;
    const showBottomBar = !workspaceAsset?.anonymousOptions?.hideReportAndNote;
    return (
      <View style={styles.mainContainer}>
        <ScrollView
          style={styles.scrollContainer}
          contentContainerStyle={[
            styles.scrollContent,
            showBottomBar && styles.scrollContentWithBottomBar,
          ]}
        >
          <View style={styles.logo}>
            <Logo />
          </View>
          <View style={[styles.container, isMobile && styles.containerMobile]}>
            <TopComponentBarCropCircle title={workspaceAsset.asset.name} />
            <View style={styles.asset}>
              <AssetHeader
                workspaceAsset={workspaceAsset}
                assetDetailsContainerStyle={styles.assetDetailsContainer}
              />
            </View>
            <View style={styles.sectionContainer}>
              <View style={styles.activityListHeaderContainer}>
                <Text variant="titleMedium" style={styles.activitiesTitle}>
                  {t('routines.header.title')}
                </Text>
              </View>
              <View style={styles.routinesContainer}>
                <RoutineButtons
                  workspaceAsset={workspaceAsset}
                  onRoutineSelected={onRoutineSelected}
                  showAll={true}
                  initialRoutinesToShow={3}
                  hideAssignRoutinesButton={true}
                />
              </View>
            </View>
          </View>
        </ScrollView>
        {showBottomBar && (
          <View style={styles.bottomBarAnchor}>
            <BottomComponentBar
              additionalHeaderBlock={
                <ActionButtons
                  asset={props.asset}
                  onCompleted={onIssueCompleted}
                  workspaceId={workspaceAsset.workspace.id}
                />
              }
            />
          </View>
        )}
      </View>
    );
  };

  // Pre-#613 layout: logo + asset card + (optional) Report/Note buttons above
  // the routines list. No top bar, no floating bottom bar.
  const renderLegacyBody = () =>
    workspaceAsset && (
      <View style={legacyStyles.mainContainer}>
        <View
          style={[
            legacyStyles.container,
            isMobile && legacyStyles.containerMobile,
          ]}
        >
          <View style={legacyStyles.logo}>
            <Logo />
          </View>
          <View style={legacyStyles.asset}>
            <AssetHeader
              workspaceAsset={workspaceAsset}
              assetDetailsContainerStyle={legacyStyles.assetDetailsContainer}
            />
          </View>
          {!workspaceAsset?.anonymousOptions?.hideReportAndNote && (
            <View style={legacyStyles.actionButons}>
              <ActionButtons
                asset={props.asset}
                onCompleted={onIssueCompleted}
                workspaceId={workspaceAsset.workspace.id}
                buttonStyle={legacyStyles.buttonStyle}
                titleFontSize={12}
              />
            </View>
          )}
          <View style={legacyStyles.sectionContainer}>
            <View style={legacyStyles.activityListHeaderContainer}>
              <Text variant="titleMedium" style={legacyStyles.activitiesTitle}>
                {t('routines.header.title')}
              </Text>
            </View>
            <View style={legacyStyles.routinesContainer}>
              <RoutineButtons
                workspaceAsset={workspaceAsset}
                onRoutineSelected={onRoutineSelected}
                showAll={true}
                initialRoutinesToShow={3}
                hideAssignRoutinesButton={true}
              />
            </View>
          </View>
        </View>
      </View>
    );

  return (
    <PaperProvider theme={BaseTheme}>
      <SafeAreaProvider>
        <KeyboardSafeView style={styles.containerKeyboard}>
          <GrippContext.Provider
            value={{
              logger: SentryWebLogger,
              secureStorageHandler: localStorageHandler,
              storageHandler: localStorageHandler,
              mediaPickerComponent: MediaPicker,
              signaturePadScreen: SignatureScreenWeb,
            }}
          >
            {showReported ? (
              <AnonymousReported
                message={reportedMessage}
                anonymousIssueIds={anonymousIssueIds}
              />
            ) : isCropCircleRedesignEnabled ? (
              renderCropCircleBody()
            ) : (
              renderLegacyBody()
            )}
            {routineExecutionProps && (
              <RoutineModal
                {...routineExecutionProps}
                visible={executeModalVisible}
                confirmCancel={true}
                onCanceled={() => {
                  setExecuteModalVisible(false);
                }}
                onCompleted={async () => {
                  setExecuteModalVisible(false);
                  await onRoutineCompleted();
                }}
                createdFrom={IssueCreationSource.AnonymousWeb}
              />
            )}
          </GrippContext.Provider>
        </KeyboardSafeView>
      </SafeAreaProvider>
    </PaperProvider>
  );
};

const styles = StyleSheet.create({
  containerKeyboard: {
    ...Layout.container,
  },
  mainContainer: {
    flex: 1,
    flexDirection: 'column',
    backgroundColor: tokens.colors.neutral[100],
    width: '100%',
    marginHorizontal: 0,
    paddingHorizontal: 0,
  },
  scrollContainer: {
    flex: 1,
    width: '100%',
  },
  scrollContent: {
    alignItems: 'center',
  },
  // Reserved space for the floating BottomComponentBar so the last routine
  // isn't hidden beneath it. Only applied when the bar renders.
  scrollContentWithBottomBar: {
    paddingBottom: 110,
  },
  container: {
    minWidth: '60%',
    maxWidth: 800,
    width: '100%',
  },
  containerMobile: {
    width: '100%',
    maxWidth: '100%',
  },
  routinesContainer: {
    flex: 1,
  },
  logo: {
    paddingHorizontal: 20,
    paddingVertical: 20,
    width: '100%',
    alignItems: 'flex-start',
    justifyContent: 'center',
    borderBottomColor: Colors.grayDisabled,
    borderBottomWidth: 0.5,
  },
  asset: {
    paddingVertical: 20,
    paddingLeft: 20,
    backgroundColor: Colors.white,
    borderRadius: 8,
    marginHorizontal: 20,
    marginTop: 10,
    marginBottom: 10,
  },
  sectionContainer: {
    marginBottom: 24,
    marginHorizontal: Spacing.baseMargin.marginHorizontal,
  },
  activitiesTitle: {
    ...tokens.typography.body.mediumEmphasised,
    color: tokens.colors.neutral[800],
    textAlignVertical: 'top',
  },
  activityListHeaderContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: tokens.spacing.md,
    height: 20,
  },
  assetDetailsContainer: {
    justifyContent: 'center',
  },
  // Establishes a non-collapsed positioned containing block so the
  // BottomComponentBar's inner `position: absolute, width: 100%` resolves
  // against full viewport width instead of a 0-width Appbar root layer.
  bottomBarAnchor: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 1000,
  },
});

// Pre-#613 layout styles, restored verbatim (typo `actionButons` included).
const legacyStyles = StyleSheet.create({
  mainContainer: {
    flex: 1,
    flexDirection: 'column',
    backgroundColor: Colors.primaryGrayBackground,
    width: '100%',
    marginHorizontal: 0,
    paddingHorizontal: 0,
    alignItems: 'center',
  },
  container: {
    minWidth: '60%',
    maxWidth: 800,
  },
  containerMobile: {
    width: '100%',
    maxWidth: '100%',
  },
  routinesContainer: {
    flex: 1,
  },
  logo: {
    paddingHorizontal: 20,
    paddingVertical: 20,
    alignItems: 'flex-start',
    justifyContent: 'center',
    borderBottomColor: Colors.grayDisabled,
    borderBottomWidth: 0.5,
  },
  asset: {
    paddingVertical: 20,
    paddingLeft: 20,
    backgroundColor: Colors.white,
    borderRadius: 8,
    marginHorizontal: 20,
    marginTop: 20,
    marginBottom: 10,
  },
  actionButons: {
    paddingRight: 20,
  },
  sectionContainer: {
    marginBottom: 24,
    marginHorizontal: Spacing.baseMargin.marginHorizontal,
  },
  activitiesTitle: {
    ...tokens.typography.body.mediumEmphasised,
    color: tokens.colors.neutral[800],
    textAlignVertical: 'top',
  },
  activityListHeaderContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: tokens.spacing.md,
    height: 20,
  },
  assetDetailsContainer: {
    justifyContent: 'center',
  },
  buttonStyle: {
    maxWidth: '100%',
    fontSize: 12,
  },
});
